import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVendors } from '../context/VendorsContext';
import { useWebsiteContent } from '../context/WebsiteContentContext';
import { fetchSummary, fetchPopularPages } from '../services/analyticsService';
import { getCachedData, setCachedData } from '../services/analyticsCache';
import { fetchNewActivity, markActivityAsSeen } from '../services/adminActivityService';
import { formatDate } from '../utils/formatters';
import Layout from '../components/Layout';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const isStakeholder = role === 'stakeholder';
  const { vendors } = useVendors();
  const { contentBlocks } = useWebsiteContent();
  const [newActivity, setNewActivity] = useState({ newClients: [], newProducts: [], newAmazonRegistry: [] });
  const [activityLoading, setActivityLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVendors: 0,
    pageViewsLast7Days: 0,
    uniqueVisitorsLast7Days: 0,
    totalPageViews: 0,
    topPage: '/',
    topPageViews: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Update vendor count when vendors load
    setStats(prev => ({
      ...prev,
      totalVendors: vendors.length
    }));
  }, [vendors]);

  useEffect(() => {
    // Load analytics summary data (7 days)
    const loadAnalyticsData = async () => {
      setLoading(true);
      setError(null);
      const days = 7;

      try {
        // Check cache first
        const cachedSummary = getCachedData('summary', days);
        const cachedPopularPages = getCachedData('popularPages', days);

        if (cachedSummary && cachedPopularPages) {
          console.log('Using cached analytics data for Dashboard');
          setStats(prev => ({
            ...prev,
            pageViewsLast7Days: cachedSummary.totalViews || 0,
            uniqueVisitorsLast7Days: cachedSummary.uniqueUsers || 0,
            totalPageViews: cachedSummary.totalViews || 0,
            topPage: cachedPopularPages[0]?.page || '/',
            topPageViews: cachedPopularPages[0]?.views || 0,
          }));
          setLoading(false);
          return;
        }

        // Fetch only summary and popular pages (efficient)
        const [summary, popularPages] = await Promise.all([
          fetchSummary(days),
          fetchPopularPages(days),
        ]);

        // Cache the data
        setCachedData('summary', days, summary);
        setCachedData('popularPages', days, popularPages);

        setStats(prev => ({
          ...prev,
          pageViewsLast7Days: summary.totalViews || 0,
          uniqueVisitorsLast7Days: summary.uniqueUsers || 0,
          totalPageViews: summary.totalViews || 0,
          topPage: popularPages[0]?.page || '/',
          topPageViews: popularPages[0]?.views || 0,
        }));
      } catch (err) {
        console.error('Error loading analytics:', err);
        setError(err.message);
        // Keep default values (0) if error
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setActivityLoading(false);
      return;
    }
    const load = async () => {
      setActivityLoading(true);
      try {
        const data = await fetchNewActivity(user.id);
        setNewActivity(data);
      } catch (err) {
        console.error('Error loading new activity:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    load();
  }, [user?.id]);

  const handleActivityClick = async (entityType, entityId, clientId, path) => {
    if (!user?.id) return;
    await markActivityAsSeen(user.id, entityType, entityId);
    setNewActivity((prev) => ({
      newClients: entityType === 'client_profile' ? prev.newClients.filter((c) => c.id !== entityId) : prev.newClients,
      newProducts: entityType === 'client_product' ? prev.newProducts.filter((p) => p.id !== entityId) : prev.newProducts,
      newAmazonRegistry: entityType === 'amazon_registry' ? prev.newAmazonRegistry.filter((r) => r.id !== entityId) : prev.newAmazonRegistry,
    }));
    navigate(path);
  };

  const activityItemsSorted = React.useMemo(() => {
    const clients = (newActivity.newClients || []).map((c) => ({
      type: 'client_profile',
      id: c.id,
      clientId: null,
      label: c.label,
      clientName: null,
      createdAt: c.createdAt,
      path: `/clients/${c.id}`,
    }));
    const products = (newActivity.newProducts || []).map((p) => ({
      type: 'client_product',
      id: p.id,
      clientId: p.clientId,
      label: p.label,
      clientName: p.clientName,
      createdAt: p.createdAt,
      path: `/clients/${p.clientId}/product/${p.id}`,
    }));
    const registries = (newActivity.newAmazonRegistry || []).map((r) => ({
      type: 'amazon_registry',
      id: r.id,
      clientId: r.clientId,
      label: r.clientName,
      clientName: null,
      createdAt: r.createdAt,
      path: `/clients/${r.clientId}`,
    }));
    return [...clients, ...products, ...registries].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [newActivity.newClients, newActivity.newProducts, newActivity.newAmazonRegistry]);

  if (isStakeholder) {
    return (
      <Layout>
        <div className="dashboard">
          <div className="dashboard-header-section">
            <h1>Dashboard Overview</h1>
            <p>Welcome back. You can view the clients assigned to you and their activity below.</p>
          </div>

          <div className="dashboard-grid">
            <Link to="/clients" className="dashboard-card action-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="action-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3>View clients</h3>
                <p>Open the clients you have access to</p>
              </div>
            </Link>

            <div className="dashboard-card dashboard-card-activity">
              <h3>What&apos;s new</h3>
              {activityLoading ? (
                <div className="activity-list-loading">Loading...</div>
              ) : activityItemsSorted.length === 0 ? (
                <div className="activity-list-empty">No new activity</div>
              ) : (
                <ul className="activity-list">
                  {activityItemsSorted.map((item) => (
                    <li key={`${item.type}-${item.id}`} className="activity-item">
                      <button
                        type="button"
                        className="activity-item-link"
                        onClick={() => handleActivityClick(item.type, item.id, item.clientId, item.path)}
                      >
                        <span className="activity-item-label">
                          {item.type === 'client_profile' ? 'New client: ' : item.type === 'amazon_registry' ? 'Submitted Amazon Brand Registry form: ' : 'New product: '}
                          {item.label}
                          {item.clientName && <span className="activity-item-client"> ({item.clientName})</span>}
                        </span>
                        <span className="activity-badge">New</span>
                        <span className="activity-item-meta">{formatDate(item.createdAt)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header-section">
          <h1>Dashboard Overview</h1>
          <p>Welcome back! Here's what's happening with your site</p>
        </div>

        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon stat-icon-yellow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Vendrs</p>
              <h2 className="stat-value">{stats.totalVendors}</h2>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Page Views (7 Days)</p>
              <h2 className="stat-value">
                {loading ? '...' : stats.pageViewsLast7Days.toLocaleString()}
              </h2>
              {error && <span className="stat-error" style={{ fontSize: '0.75rem', color: '#dc3545' }}>Unable to load</span>}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-green">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Unique Visitors (7 Days)</p>
              <h2 className="stat-value">
                {loading ? '...' : stats.uniqueVisitorsLast7Days.toLocaleString()}
              </h2>
              {error && <span className="stat-error" style={{ fontSize: '0.75rem', color: '#dc3545' }}>Unable to load</span>}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Page Views</p>
              <h2 className="stat-value">
                {loading ? '...' : stats.totalPageViews.toLocaleString()}
              </h2>
              {error && <span className="stat-error" style={{ fontSize: '0.75rem', color: '#dc3545' }}>Unable to load</span>}
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Top Performing Page</h3>
            <div className="top-page">
              {loading ? (
                <div className="page-info">
                  <span className="page-path">Loading...</span>
                </div>
              ) : error ? (
                <div className="page-info">
                  <span className="page-path" style={{ color: '#dc3545' }}>Unable to load</span>
                </div>
              ) : (
                <div className="page-info">
                  <span className="page-path">{stats.topPage || '/'}</span>
                  <span className="page-views">{stats.topPageViews} views (last 7 days)</span>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Content Blocks</h3>
            <div className="content-info">
              <p><strong>{contentBlocks.length}</strong> editable content blocks</p>
              <Link to="/website" className="card-link">Manage Content →</Link>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-activity">
            <h3>What&apos;s new</h3>
            {activityLoading ? (
              <div className="activity-list-loading">Loading...</div>
            ) : activityItemsSorted.length === 0 ? (
              <div className="activity-list-empty">No new activity</div>
            ) : (
              <ul className="activity-list">
                {activityItemsSorted.map((item) => (
                  <li key={`${item.type}-${item.id}`} className="activity-item">
                    <button
                      type="button"
                      className="activity-item-link"
                      onClick={() => handleActivityClick(item.type, item.id, item.clientId, item.path)}
                    >
                      <span className="activity-item-label">
                        {item.type === 'client_profile' ? 'New client: ' : 'New product: '}
                        {item.label}
                        {item.clientName && <span className="activity-item-client"> ({item.clientName})</span>}
                      </span>
                      <span className="activity-badge">New</span>
                      <span className="activity-item-meta">{formatDate(item.createdAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="dashboard-actions">
          <Link to="/vendors" className="action-card">
            <div className="action-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h3>Manage Vendrs</h3>
              <p>View, create, edit, and reorder your vendrs</p>
            </div>
          </Link>

          <Link to="/website" className="action-card">
            <div className="action-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3>Edit Website Content</h3>
              <p>Update text and copy across all website pages</p>
            </div>
          </Link>

          <Link to="/analytics" className="action-card">
            <div className="action-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3>View Analytics</h3>
              <p>Detailed insights into website traffic and performance</p>
            </div>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;

